import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { NAV_ROUTES } from '../../constants/navRoutes';
import { NavGroup, NavModule, NavSubmodule, NavTree, SideNavProps } from './types';
import MobileNavBar from './MobileNavBar';
import DesktopNavBar from './DesktopNavBar';
import { useLazyGetBannersQuery } from '@api/banner';
import { usePermission } from '@hooks/usePermission';
import {
  buildNavTree,
  buildUngroupedModules,
  getApiSubmodules,
  getAdminOnlySubmodules,
  isDemoNavigation,
  isNavGroupActive,
  isNavModuleActive,
  isNavSubmoduleActive,
} from './sideNavUtils';

const SideNav: React.FC<SideNavProps> = ({
  customWidth,
  options,
  isLoading,
  isAdmin,
  username,
  onClickLogout,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [openKeys, setOpenKeys] = useState<string[]>([]);
  const { isAuthenticated } = usePermission();
  const [triggerGetBanners, { data: bannerResponse }] = useLazyGetBannersQuery();
  const activeBanners = bannerResponse?.data?.filter((banner) => banner.active);

  const { groups: navGroups, ungroupedModules } = useMemo((): NavTree => {
    if (isLoading) return { groups: [], ungroupedModules: [] };

    const submodules = getApiSubmodules(options);
    // Demo modules are shown as they come, without grouping
    if (isDemoNavigation(submodules)) {
      return { groups: [], ungroupedModules: buildUngroupedModules(submodules) };
    }

    // Admin-only frontend pages join the backend submodules only for admins
    if (isAdmin) submodules.push(...getAdminOnlySubmodules());

    return buildNavTree(submodules);
  }, [isLoading, options, isAdmin]);

  useEffect(() => {
    if (isAuthenticated) {
      triggerGetBanners();
    }
  }, [isAuthenticated]);

  const isCurrentRoute = (path: string | string[]): boolean => {
    if (Array.isArray(path)) {
      const id = location.pathname.split('/').pop() || '';
      return id ? path.includes(id) : false;
    }

    // For getting started the absolute path is checked.
    if (path.includes(NAV_ROUTES.GETTING_STARTED)) {
      return location.pathname === path;
    }

    return location.pathname.includes(path);
  };

  const toggleOpenKey = (key: string) => {
    setOpenKeys((prev) =>
      prev.includes(key) ? prev.filter((openKey) => openKey !== key) : [...prev, key],
    );
  };

  const isGroupActive = (group: NavGroup) => isNavGroupActive(group, isCurrentRoute);
  const isModuleActive = (module: NavModule) => isNavModuleActive(module, isCurrentRoute);
  const isSubmoduleActive = (submodule: NavSubmodule) =>
    isNavSubmoduleActive(submodule, isCurrentRoute);

  const handleSubmoduleClick = (submodule: NavSubmodule) => {
    navigate(submodule.href);
  };

  // Opens the module and lands on its first submodule, or closes it when one of its submodules is
  // already active. Returns true when it navigated.
  const handleModuleClick = (module: NavModule): boolean => {
    const isOpen = openKeys.includes(module.key);
    const hasActiveChild = isModuleActive(module);

    if (isOpen && hasActiveChild) {
      toggleOpenKey(module.key);
      return false;
    }

    if (!isOpen) toggleOpenKey(module.key);
    if (hasActiveChild) return false;

    navigate(module.submodules[0].href);
    return true;
  };

  return (
    <>
      {/* Mobile View */}
      <MobileNavBar
        navGroups={navGroups}
        ungroupedModules={ungroupedModules}
        openKeys={openKeys}
        onToggleGroup={toggleOpenKey}
        onModuleClick={handleModuleClick}
        onSubmoduleClick={handleSubmoduleClick}
        isGroupActive={isGroupActive}
        isModuleActive={isModuleActive}
        isSubmoduleActive={isSubmoduleActive}
        userName={username}
        customWidth={customWidth}
        onClickLogout={onClickLogout}
        activeBanners={activeBanners}
      />

      {/* Desktop View */}
      <DesktopNavBar
        navGroups={navGroups}
        ungroupedModules={ungroupedModules}
        openKeys={openKeys}
        onToggleGroup={toggleOpenKey}
        onModuleClick={handleModuleClick}
        onSubmoduleClick={handleSubmoduleClick}
        isGroupActive={isGroupActive}
        isModuleActive={isModuleActive}
        isSubmoduleActive={isSubmoduleActive}
        userName={username}
        customWidth={customWidth}
        onClickLogout={onClickLogout}
        activeBanners={activeBanners}
      />
    </>
  );
};

export default SideNav;
